import React, { useEffect, useMemo, useRef } from 'react';
import { Drawer, Loader, Box } from '@mantine/core';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { ShowMessage } from '../common';
import { SurveyHistoryDrawerHeader } from './SurveyHistoryDrawerHeader';
import { SurveyHistoryPcrList } from './SurveyHistoryPcrList';
import { GET_SURVEY_HISTORY_DRAWER } from '../../apollo/queries/survey';
import { useRequestDrawer } from '../../context/RequestDrawerContext';
import {
    getSurveyScopeLabel,
    getSurveyScopeStores,
} from '../../pages/price-surveys/helpers';

interface SurveyHistoryDrawerProps {
    surveyId: string;
    opened: boolean;
    onClose: () => void;
}

interface SurveyItemWithPcr {
    id: string;
    priceChangeRequestId: string | null;
    priceChangeRequest: {
        id: string;
        friendlyId?: number | null;
        createdAt: string;
        latestStatus: string;
    } | null;
}

export function SurveyHistoryDrawer({
    surveyId,
    opened,
    onClose,
}: SurveyHistoryDrawerProps) {
    const { isOpen: isRequestDrawerOpen } = useRequestDrawer();
    const prevRequestDrawerOpenRef = useRef(false);

    const {
        data: surveyData,
        loading: surveyLoading,
        error: surveyError,
        refetch,
    } = useQuery(GET_SURVEY_HISTORY_DRAWER, {
        variables: { id: surveyId },
        skip: !opened || !surveyId,
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (
            prevRequestDrawerOpenRef.current &&
            !isRequestDrawerOpen &&
            opened
        ) {
            refetch().catch(err => {
                console.error('Failed to refetch survey history:', err);
            });
        }
        prevRequestDrawerOpenRef.current = isRequestDrawerOpen;
    }, [isRequestDrawerOpen, opened, refetch]);

    const survey = surveyData?.getSurvey;
    const zoneName = getSurveyScopeLabel(survey) || 'Unknown Zone';
    const stores = getSurveyScopeStores(survey);
    const championStoreId = survey?.championUser?.managerStore?.id ?? null;
    const surveyPeriod = survey
        ? `${dayjs(survey.createdAt).format('MMM D')} - ${dayjs(
              survey.dueBy,
          ).format('MMM D, YYYY')}`
        : '—';

    const pcrItems = useMemo(() => {
        const items = (survey?.surveyItems ?? []) as SurveyItemWithPcr[];
        return items
            .filter(si => si.priceChangeRequestId && si.priceChangeRequest)
            .map(si => ({
                id: si.priceChangeRequest!.id,
                friendlyId: si.priceChangeRequest!.friendlyId,
                surveyItemId: si.id,
                latestStatus: si.priceChangeRequest!.latestStatus,
                createdAt: si.priceChangeRequest!.createdAt,
            }));
    }, [survey?.surveyItems]);

    function renderContent() {
        if (!surveyId) {
            return (
                <ShowMessage when variant="muted">
                    No survey selected.
                </ShowMessage>
            );
        }

        if (surveyLoading) {
            return (
                <Box className="flex justify-center items-center py-10">
                    <Loader />
                </Box>
            );
        }

        if (surveyError) {
            return (
                <ShowMessage when variant="error">
                    Error loading survey — please try again or contact support.
                </ShowMessage>
            );
        }

        if (!pcrItems.length) {
            return (
                <ShowMessage when variant="muted">
                    No price change requests from this survey.
                </ShowMessage>
            );
        }

        return <SurveyHistoryPcrList pcrItems={pcrItems} />;
    }

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            title={
                <SurveyHistoryDrawerHeader
                    zoneName={zoneName}
                    stores={stores}
                    surveyPeriod={surveyPeriod}
                    championStoreId={championStoreId}
                />
            }
            position="right"
            size="xl"
            padding={0}
            classNames={{
                header: 'items-start p-6 pb-4 border-b border-gray-200 mb-0 mr-0 shrink-0 bg-[#F2F3F5]',
                body: 'p-0 flex flex-col flex-1 min-h-0 overflow-hidden bg-[#F2F3F5]',
                close: 'mt-1',
                content: 'flex flex-col max-h-full bg-[#F2F3F5]',
            }}
        >
            <Box className="flex-1 min-h-0 overflow-y-auto p-6 bg-[#F2F3F5]">
                {renderContent()}
            </Box>
        </Drawer>
    );
}
